import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessagesSquare, Quote, MousePointerClick, X } from "lucide-react";

const FEEDBACK_POOL = [
  "Loved how fast the results updated the moment everyone voted.",
  "The Q&A round kept the whole room engaged till the very end.",
  "Would love more breakout time between the poll rounds.",
  "The live leaderboard made the quiz genuinely competitive.",
  "Visuals were sharp, colorful, and easy to read from the back row.",
  "Joining with just a code felt effortless on my phone.",
  "The speaker paced the session really well throughout.",
  "More real-world examples in the next session, please.",
  "Everyone had a voice this time, not just the loudest ones.",
  "The gauge chart was such a nice touch for the yes/no question.",
];

export function OpenTextShowcase() {
  const [visible, setVisible] = useState(2);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const id = setInterval(() => {
      setVisible((v) => (v >= FEEDBACK_POOL.length ? 2 : v + 1));
    }, 1300);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setActiveIndex((cur) => {
        if (cur !== null) return null;
        return Math.floor(Math.random() * Math.max(visible, 1));
      });
    }, 4200);
    return () => clearInterval(id);
  }, [visible]);

  return (
    <section className="px-6 max-w-7xl mx-auto pb-28" data-testid="section-open-text-showcase">
      <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#4F46E5] bg-[#4F46E5]/10 px-3 py-1.5 rounded-full mb-4">
        <MessagesSquare className="w-3.5 h-3.5" /> New: Open text feedback
      </span>
      <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3">Every voice, its own card</h2>
      <p className="text-slate-600 max-w-2xl mb-12">
        Ask an open question and every response lands live as its own card on a feedback wall — no averaging, no word clouds.
        Click any card to pop it out full-screen, then close it and pick another.
      </p>

      <div className="relative rounded-3xl bg-[#0B0F19] border border-white/10 shadow-[0_25px_60px_rgba(15,23,42,0.3)] p-6 sm:p-8 overflow-hidden" data-testid="open-text-showcase-wall">
        <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full bg-[#8B5CF6]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full bg-[#4F46E5]/15 blur-3xl pointer-events-none" />
        <div className="relative z-10 flex items-center justify-between mb-5">
          <p className="text-white/40 text-sm font-medium">"What's one thing we should improve about our next team offsite?"</p>
          <span className="hidden sm:flex items-center gap-1.5 text-xs text-white/30 shrink-0 ml-4">
            <MousePointerClick className="w-3.5 h-3.5" /> Click a card
          </span>
        </div>

        <div className="relative z-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 content-start items-start auto-rows-min gap-3 min-h-[220px]">
          {FEEDBACK_POOL.slice(0, visible).map((text, i) => (
            <motion.button
              key={i}
              layoutId={`feedback-card-${i}`}
              onClick={() => setActiveIndex(i)}
              data-testid={`open-text-showcase-card-${i}`}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: activeIndex === i ? 0 : 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              whileHover={{ scale: 1.03, y: -2 }}
              className="text-left rounded-2xl border border-white/10 bg-white/[0.04] hover:border-[#8B5CF6]/50 hover:bg-white/[0.07] p-4 flex flex-col justify-between min-h-[100px] max-h-[140px]"
            >
              <p className="text-sm text-white/90 leading-snug line-clamp-4">{text}</p>
              <p className="text-[10px] mt-2 font-semibold uppercase tracking-wider text-white/30">Participant {i + 1}</p>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {activeIndex !== null && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveIndex(null)}
            data-testid="open-text-showcase-modal"
          >
            <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div
              layoutId={`feedback-card-${activeIndex}`}
              transition={{ type: "spring", damping: 24, stiffness: 220 }}
              onClick={(e) => e.stopPropagation()}
              className="relative z-10 w-full max-w-2xl rounded-3xl border border-white/10 bg-[#0B0F19] p-8 sm:p-10 overflow-hidden"
            >
              <motion.div
                className="absolute -inset-1 rounded-3xl blur-2xl opacity-25 pointer-events-none bg-gradient-to-br from-[#8B5CF6] to-[#4F46E5]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.25 }}
                transition={{ delay: 0.15 }}
              />
              <button
                onClick={() => setActiveIndex(null)}
                data-testid="open-text-showcase-close-button"
                className="absolute top-4 right-4 rounded-full p-1.5 text-white/40 hover:text-white hover:bg-white/10 z-20"
              >
                <X className="w-4 h-4" />
              </button>
              <motion.div className="relative z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
                <Quote className="w-8 h-8 mb-3 opacity-50 text-[#8B5CF6]" />
                <p className="font-heading text-2xl sm:text-3xl font-semibold leading-tight text-white">{FEEDBACK_POOL[activeIndex]}</p>
                <p className="mt-5 text-sm font-semibold uppercase tracking-wider text-white/40">Participant {activeIndex + 1}</p>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
